import type { ImageLoader, ImageLoaderProps } from 'next/image';

// Contentful's Images API rejects widths above this on either axis.
const CONTENTFUL_MAX_WIDTH = 4000;
const DEFAULT_QUALITY = 75;

/**
 * `next.config.js` `images.loaderFile`: for Contentful assets, delegates
 * resizing/format conversion to the Contentful Images API instead of
 * `/_next/image` (sharp). Oversized Contentful originals (30-50+ MP) can OOM
 * the Node process when sharp decodes them regardless of the requested
 * width — see LPOT-1674. Non-Contentful sources pass through unmodified.
 *
 * Next's own default loader skips optimization for `.svg` sources to avoid
 * rasterizing vector assets, but that special-casing only applies to the
 * default loader, not a custom one — so it has to be replicated here, or
 * `fm=webp` rasterizes SVGs and they render pixelated.
 */
const contentfulLoader: ImageLoader = ({
  src,
  width,
  quality,
}: ImageLoaderProps) => {
  if (!src.includes('ctfassets.net')) {
    return src;
  }

  const url = new URL(src);

  if (url.pathname.toLowerCase().endsWith('.svg')) {
    return src;
  }

  url.searchParams.set('w', String(Math.min(width, CONTENTFUL_MAX_WIDTH)));
  url.searchParams.set('q', String(quality ?? DEFAULT_QUALITY));
  url.searchParams.set('fm', 'webp');

  return url.toString();
};

export default contentfulLoader;
